import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { InformationCircleIcon, LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'

import { ConsentCheckbox } from '../ConsentCheckbox'
import { ConsentSection } from '../ConsentSection'
import { InfoPanel } from '../InfoPanel'
import { ConsentSectionFieldsProps } from '../types'

export function DataSharingConsentSection({ consents, updateConsent }: ConsentSectionFieldsProps) {
  return (
    <ConsentSection title={t`Security and Sharing of Your Data`} required>
      <div className="flex items-start gap-2">
        <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
        <p className="text-sm text-gray-600">
          <Trans>To provide you with a secure and high-quality teleconsultation, some of your data is shared with the following service providers:</Trans>
        </p>
      </div>
      <HealthDataHostingInfoPanel />
      <VideoConferencingInfoPanel />
      <PaymentProcessingInfoPanel />
      <ProviderComplianceNotice />
      <HealthDataSecurityLink />
      <ConsentCheckbox
        name="understoodDataSharing"
        label={t`I have read and understood the information regarding the sharing of my data with the third parties listed above.`}
        checked={consents.understoodDataSharing}
        onChange={(checked) => updateConsent('understoodDataSharing', checked)}
        required
      />
    </ConsentSection>
  )
}

function HealthDataHostingInfoPanel() {
  return (
    <InfoPanel title={t`AWS (Amazon Web Services) — Health data hosting`} defaultOpen>
      <div className="flex flex-col gap-2">
        <p>
          <Trans>
            All your health data (medical records, documents, recordings) is hosted by AWS, certified as a Health Data Host (HDS) in accordance with French
            regulations. This certification guarantees the confidentiality, integrity and availability of your health data.
          </Trans>
        </p>
        <p className="font-semibold text-blue-900">
          <Trans>Purpose: Secure storage and availability of your health data.</Trans>
        </p>
        <p className="text-blue-800">
          <Trans>All recordings, documents and images are stored on AWS S3 with AES-256 server-side encryption enabled by default.</Trans>
        </p>
        <a className="text-xs font-medium text-primary underline-offset-4 hover:underline" href="https://aws.amazon.com/fr/compliance/hds/" target="_blank" rel="noopener noreferrer">
          <Trans>Learn more about AWS HDS certification</Trans>
        </a>
      </div>
    </InfoPanel>
  )
}

function VideoConferencingInfoPanel() {
  return (
    <InfoPanel title={t`Amazon Chime SDK — Video conferencing`}>
      <div className="flex flex-col gap-2">
        <p>
          <Trans>
            Video and audio streams during the teleconsultation are transmitted via the Amazon Chime SDK. Streams are encrypted in transit using DTLS-SRTP
            protocols, following industry standard cryptographic protocols.
          </Trans>
        </p>
        <p className="font-semibold text-blue-900">
          <Trans>Purpose: Secure real-time audiovisual communication between you and your healthcare professional.</Trans>
        </p>
        <p className="italic">
          <Trans>No health data is stored by this service.</Trans>
        </p>
        <a className="text-xs font-medium text-primary underline-offset-4 hover:underline" href="https://aws.amazon.com/blogs/business-productivity/understanding-security-in-the-amazon-chime-application-and-sdk/" target="_blank" rel="noopener noreferrer">
          <Trans>Learn more about Amazon Chime SDK security</Trans>
        </a>
      </div>
    </InfoPanel>
  )
}

function PaymentProcessingInfoPanel() {
  return (
    <InfoPanel title={t`Stripe — Payment processing`}>
      <div className="flex flex-col gap-2">
        <p>
          <Trans>Payment data (name, billing details) is processed by Stripe, a PCI DSS certified payment provider. No health data is shared with this provider.</Trans>
        </p>
        <p className="font-semibold text-blue-900">
          <Trans>Purpose: Secure processing of payments related to your teleconsultation.</Trans>
        </p>
      </div>
    </InfoPanel>
  )
}

function ProviderComplianceNotice() {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
      <InformationCircleIcon className="mt-0.5 h-4 w-4 text-blue-600" aria-hidden="true" />
      <p className="text-xs text-blue-900">
        <Trans>These third parties are contractually bound to process your data solely for the purposes described above and in compliance with the GDPR.</Trans>
      </p>
    </div>
  )
}

function HealthDataSecurityLink() {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3">
      <LockClosedIcon className="mt-0.5 h-4 w-4 text-green-600" aria-hidden="true" />
      <p className="text-xs text-green-900">
        <Trans>For more information on health data security standards, visit the</Trans>{' '}
        <a className="font-bold underline-offset-4 hover:underline" href="https://esante.gouv.fr" target="_blank" rel="noopener noreferrer">
          <Trans>Agence du Numérique en Santé (ANS)</Trans>
        </a>
        .
      </p>
    </div>
  )
}
